/**
 * チェックリストページ (8 カテゴリ 37 項目)
 *
 * カテゴリ・Principle・判定レベルでフィルタできるテーブル表示。canEdit は項目の
 * 追加・編集・論理削除 (isActive=false) ができる。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business チェックリスト項目のカテゴリ別テーブル表示・フィルタ・追加・編集・論理削除。
 * @graph-connects api [calls] checklist.listCategories / listItems / createItem / updateItem
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { ChecklistItem, DeviationLevel, PrincipleCode } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { useBrandScope } from '../components/brand-scope-context.js';
import { matchesBrandScope } from '../lib/brand-scope.js';
import { filterChecklistItems, groupChecklistItems } from '../lib/checklist-utils.js';
import {
  BrandAttributionField,
  EmptyState,
  Field,
  GhostButton,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  PrincipleTag,
  SectionHeading,
  StatusPill,
} from '../components/ui.js';

/** 編集ドラフト (新規は id: null、brandId/formatId '' = 共通)。 */
interface ItemDraft {
  id: string | null;
  text: string;
  categoryId: string;
  principleCodes: PrincipleCode[];
  deviationLevel: DeviationLevel;
  brandId: string;
  formatId: string;
}

/** Principle コードの選択肢。 @graph-connects none */
const PRINCIPLE_OPTIONS: PrincipleCode[] = ['P1', 'P2', 'P3', 'P4'];

/**
 * チェックリストページ。
 * @returns ページ要素
 * @graph-connects api [calls] checklist.listCategories / listItems / createItem / updateItem
 */
export function ChecklistPage(): JSX.Element {
  const auth = useAuth();
  const brandScope = useBrandScope();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const categoriesQuery = useQuery(orpc.checklist.listCategories.queryOptions({ input: {} }));
  const itemsQuery = useQuery(orpc.checklist.listItems.queryOptions({ input: {} }));
  const formatsQuery = useQuery(orpc.formats.list.queryOptions({ input: {} }));
  const categories = categoriesQuery.data?.items ?? [];
  const items = (itemsQuery.data?.items ?? []).filter((item) =>
    matchesBrandScope(item.brandId, brandScope.scope),
  );
  const formats = formatsQuery.data?.items ?? [];

  const [categoryFilter, setCategoryFilter] = useState('');
  const [principleFilter, setPrincipleFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [draft, setDraft] = useState<ItemDraft | null>(null);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: orpc.checklist.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.guide.key() });
  };

  const createMutation = useMutation(
    orpc.checklist.createItem.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        setDraft(null);
        showToast('success', 'チェック項目を追加しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );
  const updateMutation = useMutation(
    orpc.checklist.updateItem.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        setDraft(null);
        showToast('success', 'チェック項目を更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const filtered = filterChecklistItems(items, {
    categoryId: categoryFilter === '' ? undefined : categoryFilter,
    principleCode: principleFilter === '' ? undefined : (principleFilter as PrincipleCode),
    deviationLevel: levelFilter === '' ? undefined : (levelFilter as DeviationLevel),
  });
  const groups = groupChecklistItems(categories, filtered);

  const deactivateItem = (item: ChecklistItem): void => {
    updateMutation.mutate({ id: item.id, patch: { isActive: false } });
  };

  return (
    <div>
      <SectionHeading
        title={`Checklist — ${String(filtered.length)} items`}
        action={
          auth.canEdit ? (
            <GhostButton
              onClick={() => {
                setDraft({
                  id: null,
                  text: '',
                  categoryId: categories[0]?.id ?? '',
                  principleCodes: ['P1'],
                  deviationLevel: 'ng',
                  brandId: brandScope.scope,
                  formatId: '',
                });
              }}
            >
              + 項目を追加
            </GhostButton>
          ) : null
        }
      />
      <p className="mb-4 text-body text-muted">
        「判定」列は逸脱した場合の判定。現在のステータスではない。
      </p>

      <div className="mb-5 flex gap-2">
        <select
          className={`${INPUT_CLASS} w-auto`}
          value={categoryFilter}
          onChange={(event) => {
            setCategoryFilter(event.target.value);
          }}
        >
          <option value="">全カテゴリ</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        <select
          className={`${INPUT_CLASS} w-auto`}
          value={principleFilter}
          onChange={(event) => {
            setPrincipleFilter(event.target.value);
          }}
        >
          <option value="">全 Principle</option>
          {PRINCIPLE_OPTIONS.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <select
          className={`${INPUT_CLASS} w-auto`}
          value={levelFilter}
          onChange={(event) => {
            setLevelFilter(event.target.value);
          }}
        >
          <option value="">全判定</option>
          <option value="ng">NG</option>
          <option value="caution">注意</option>
        </select>
      </div>

      {groups.length === 0 ? (
        <EmptyState message="チェック項目がありません" />
      ) : (
        groups.map((group) => (
          <div key={group.category.id} className="mb-7">
            <h3 className="mb-3 text-sm font-bold text-foreground">{group.category.name}</h3>
            <div className="dh-table-frame">
              <table className="w-full text-body">
                <thead>
                  <tr>
                    <th className="dh-th">#</th>
                    <th className="dh-th">チェック項目</th>
                    <th className="dh-th">Principle</th>
                    <th className="dh-th">判定</th>
                    {auth.canEdit && <th className="dh-th" />}
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => (
                    <tr key={item.id} className="hover:bg-glass">
                      <td className="dh-td font-mono text-muted">{item.number}</td>
                      <td className="dh-td text-subtle">{item.text}</td>
                      <td className="dh-td">
                        {item.principleCodes.map((code) => (
                          <PrincipleTag key={code} code={code} />
                        ))}
                      </td>
                      <td className="dh-td">
                        <StatusPill level={item.deviationLevel} />
                      </td>
                      {auth.canEdit && (
                        <td className="dh-td text-right">
                          <button
                            type="button"
                            className="mr-2 text-xs text-muted hover:text-foreground"
                            onClick={() => {
                              setDraft({
                                id: item.id,
                                text: item.text,
                                categoryId: item.categoryId,
                                principleCodes: [...item.principleCodes],
                                deviationLevel: item.deviationLevel,
                                brandId: item.brandId ?? '',
                                formatId: item.formatId ?? '',
                              });
                            }}
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            className="text-xs text-ng opacity-70 hover:opacity-100"
                            onClick={() => {
                              deactivateItem(item);
                            }}
                          >
                            無効化
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <Modal
        open={draft !== null}
        title={draft?.id === null ? 'チェック項目を追加' : `チェック項目を編集`}
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="チェック項目">
              <input
                className={INPUT_CLASS}
                value={draft.text}
                onChange={(event) => {
                  setDraft({ ...draft, text: event.target.value });
                }}
              />
            </Field>
            <Field label="カテゴリ">
              <select
                className={INPUT_CLASS}
                value={draft.categoryId}
                onChange={(event) => {
                  setDraft({ ...draft, categoryId: event.target.value });
                }}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Principle (複数可)">
              <div className="flex gap-3">
                {PRINCIPLE_OPTIONS.map((code) => (
                  <label key={code} className="flex items-center gap-1 text-sm text-subtle">
                    <input
                      type="checkbox"
                      checked={draft.principleCodes.includes(code)}
                      onChange={(event) => {
                        const principleCodes = event.target.checked
                          ? [...draft.principleCodes, code]
                          : draft.principleCodes.filter((current) => current !== code);
                        setDraft({ ...draft, principleCodes });
                      }}
                    />
                    {code}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="逸脱時の判定">
              <select
                className={INPUT_CLASS}
                value={draft.deviationLevel}
                onChange={(event) => {
                  setDraft({ ...draft, deviationLevel: event.target.value as DeviationLevel });
                }}
              >
                <option value="ng">NG (修正必須)</option>
                <option value="caution">注意 (デザイナーに委ねる)</option>
              </select>
            </Field>
            <BrandAttributionField
              brands={brandScope.brands}
              value={draft.brandId}
              onChange={(brandId) => {
                setDraft({ ...draft, brandId });
              }}
            />
            {formats.length > 0 && (
              <Field label="フォーマット (どの成果物に適用するか)">
                <select
                  className={INPUT_CLASS}
                  value={draft.formatId}
                  onChange={(event) => {
                    setDraft({ ...draft, formatId: event.target.value });
                  }}
                >
                  <option value="">全フォーマット共通</option>
                  {formats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={
                  draft.text === '' ||
                  draft.principleCodes.length === 0 ||
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                onClick={() => {
                  if (draft.id === null) {
                    createMutation.mutate({
                      text: draft.text,
                      categoryId: draft.categoryId,
                      principleCodes: draft.principleCodes,
                      deviationLevel: draft.deviationLevel,
                      brandId: draft.brandId === '' ? undefined : draft.brandId,
                      formatId: draft.formatId === '' ? undefined : draft.formatId,
                    });
                  } else {
                    // '' は「共通へ戻す」= null (RPC は undefined キーを運べないため)
                    updateMutation.mutate({
                      id: draft.id,
                      patch: {
                        text: draft.text,
                        categoryId: draft.categoryId,
                        principleCodes: draft.principleCodes,
                        deviationLevel: draft.deviationLevel,
                        brandId: draft.brandId === '' ? null : draft.brandId,
                        formatId: draft.formatId === '' ? null : draft.formatId,
                      },
                    });
                  }
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
